import React, { useState, useEffect, useRef } from "react";

import { Link, useHistory, useLocation } from "react-router-dom";
import { isEmpty, isString } from "lodash";
import { Trans, useTranslation } from "react-i18next";

import UploadModelCard from "../../components/Cards/UploadModelCard";
import ModelSchema from "../../schemas/model.schema";
import ModelParameterAPI from "../../API/modelParameters.api";
import { ErrorAlert, SuccessAlert } from "../../utils/Alerts";
import { longDate } from "../../utils/DateHelper";
import BreadCrumb from "../../components/Navbars/BreadCrumb";
import LazyImage from "../../components/Shared/LazyImage";
import LogCapture from "../../utils/LogManagement/LogHelper";
import { validate } from "../../utils/ValidationHelpers";
import {
    DATA_TYPES,
    MODEL_TYPE,
    FIELD_TYPES,
    OUTLIER_TYPES,
    LOGIC_TYPE,
} from "../../utils/constants";
import { uniqueModelVersionName } from "../../utils/UniqueVersionNameHelpers";
import LookupsAPI from "../../API/lookups.api";
import ModelVersionsApi from "../../API/modelVersions.api";
import {
    getNameWithoutExtension,
    objectValuesFromNumbersToString,
} from "../../utils/Helpers";
import ValidationError from "../../components/Shared/ValidationError";
import { ACTIONS, ENTITIES } from "../../utils/LogManagement/LogConstants";

const ModelUpload = () => {
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isFormValid, setIsFormValid] = useState(false);
    const [isEquationValid, setIsEquationValid] = useState(false);
    const [isFormSubmitted, setIsFormSubmitted] = useState(false);
    const [error, setError] = useState({});
    const [model, setModel] = useState({});
    const [formData, setFormData] = useState({
        QResidual: { booleanValue: false, inputValue: 4 },
        ReducedHotellingT2: { booleanValue: true, inputValue: 4 },
    });
    const [formattedEquation, setFormattedEquation] = useState({});
    const [variables, setVariables] = useState([]);
    const [isVisible, setIsVisible] = useState(true);
    const [existingModelType, setExistingModelType] = useState("");
    const [editMode, setEditMode] = useState(false);
    const [isModelInfoJson, setIsModelInfoJson] = useState(false);

    const equationWidgetRef = useRef();
    const history = useHistory();
    const location = useLocation();
    const { t } = useTranslation();

    const modelType = location.state?.modelType;
    const modelParameterVersionId = location.state?.modelParameterVersionId;

    const renderButtonForModelInfo = () => {
        if (isModelInfoJson)
            return (
                <button
                    className="secondary-green-btn mng-model modal-btn-open flex items-center gap-2"
                    onClick={() => {
                        const parsedJson = getJSON("modelInfo");
                        if (parsedJson !== "Invalid") {
                            const data = {
                                ...formData,
                                ...objectValuesFromNumbersToString(parsedJson),
                            };
                            delete data.modelInfo;
                            setFormData(data);
                            setIsModelInfoJson(false);
                        }
                    }}
                >
                    <i className="fas fa-check" />
                    {t("done")}
                </button>
            );

        return (
            <button
                className="secondary-green-btn mng-model modal-btn-open flex items-center gap-2"
                onClick={() => {
                    const data = {
                        ...formData,
                        modelInfo: JSON.stringify(
                            removeEmptyFields({
                                executionOrder: formData.executionOrder,
                                RMSECalib: formData.RMSECalib,
                                R2Calib: formData.R2Calib,
                                RMSECV: formData.RMSECV,
                                R2CV: formData.R2CV,
                                RMSETest: formData.RMSETest,
                                R2Test: formData.R2Test,
                                avgReadings: formData.avgReadings,
                                avgPredictions: formData.avgPredictions,
                                BiasCV: formData.BiasCV,
                                BiasTest: formData.BiasTest,
                                RPDCV: formData.RPDCV,
                                RPDTest: formData.RPDTest,
                                numberOfSamples: formData.numberOfSamples,
                                minValue: formData.minValue,
                                maxValue: formData.maxValue,
                            })
                        ),
                    };
                    setFormData(data);
                    setIsModelInfoJson(true);
                }}
            >
                <i className="fas fa-upload" />
                {t("importFromJson")}
            </button>
        );
    };

    const uploadModelInfoJson = () => (
        <>
            <div className="flex items-end justify-between mb-2">
                <label className="uppercase mb-0">{t("modelInfo")}</label>
                <div className="main-customer-form-bnt flex items-center justify-end">
                    {renderButtonForModelInfo()}
                </div>
            </div>

            {isModelInfoJson && (
                <div className="mb-4">
                    <textarea
                        placeholder={t("modelInfo")}
                        className="input-element textarea-element"
                        name="modelInfo"
                        onChange={(e) =>
                            setFormData((prev) => {
                                return {
                                    ...prev,
                                    modelInfo: e.target.value,
                                };
                            })
                        }
                        value={formData.modelInfo}
                    />
                    <ValidationError errorMessage={error["modelInfo"]} />
                </div>
            )}
        </>
    );

    const MODEL_OPTIONS = [
        {
            label: t("isVisible"),
            name: "isVisible",
            type: FIELD_TYPES.Switch,
            show: true,
            default: true,
            tooltip: t("isVisibleTooltip"),
        },
        {
            label: t("modelType"),
            type: FIELD_TYPES.RadioButton,
            name: "modelType",
            show: true,
            default: MODEL_TYPE.standard,
            options: [
                {
                    name: MODEL_TYPE.standard,
                    value: MODEL_TYPE.standard,
                    label: t("standard"),
                    disabled: existingModelType,
                },
                {
                    name: MODEL_TYPE.eigenvector,
                    value: MODEL_TYPE.eigenvector,
                    label: t("eigenvector"),
                    disabled: existingModelType,
                },
                {
                    name: MODEL_TYPE.calculatedFields,
                    value: MODEL_TYPE.calculatedFields,
                    label: t("calculatedFields"),
                    disabled: isEmpty(variables) || existingModelType,
                    tooltip:
                        !existingModelType && isEmpty(variables)
                            ? t("disabledCalculatedFieldsTooltip")
                            : "",
                },
            ],
            tooltip: existingModelType && t("disabledModelTypeTooltip"),
        },
        {
            label: t("versionName"),
            type: FIELD_TYPES.TextBox,
            placeholder: t("versionPlaceholder"),
            name: "version",
            show: true,
            required: true,
        },
        {
            label: t("versionDescription"),
            type: FIELD_TYPES.TextArea,
            placeholder: t("descriptionPlaceholder"),
            name: "description",
            show: true,
            required: true,
        },
        {
            element: uploadModelInfoJson,
            type: FIELD_TYPES.GridTextFields,
            name: "modelInfo",
            show: formData.modelType !== MODEL_TYPE.calculatedFields,
            horizontalLine: true,
            fields: [
                {
                    label: t("executionOrder"),
                    type: FIELD_TYPES.TextBox,
                    placeholder: t("executionOrderPlaceholder"),
                    name: "executionOrder",
                    dataType: DATA_TYPES.number,
                    show: formData.modelType !== MODEL_TYPE.calculatedFields,
                    required:
                        formData.modelType !== MODEL_TYPE.calculatedFields,
                    tooltip: t("executionOrderTooltip"),
                },
                {
                    label: t("RMSECalib"),
                    type: FIELD_TYPES.TextBox,
                    placeholder: t("RMSECalibPlaceholder"),
                    name: "RMSECalib",
                    dataType: DATA_TYPES.number,
                    show: formData.modelType !== MODEL_TYPE.calculatedFields,
                    required:
                        formData.modelType !== MODEL_TYPE.calculatedFields,
                    tooltip: t("rmseCalibrationTooltip"),
                },
                {
                    label: t("R2Calib"),
                    type: FIELD_TYPES.TextBox,
                    placeholder: t("R2CalibPlaceholder"),
                    name: "R2Calib",
                    dataType: DATA_TYPES.number,
                    show: formData.modelType !== MODEL_TYPE.calculatedFields,
                    required:
                        formData.modelType !== MODEL_TYPE.calculatedFields,
                    tooltip: t("r2CalibrationTooltip"),
                },
                {
                    label: t("rmseCv"),
                    type: FIELD_TYPES.TextBox,
                    placeholder: t("rmseCvPlaceholder"),
                    name: "RMSECV",
                    dataType: DATA_TYPES.number,
                    show: formData.modelType !== MODEL_TYPE.calculatedFields,
                    required:
                        formData.modelType !== MODEL_TYPE.calculatedFields,
                    tooltip: t("rmseCvTooltip"),
                },
                {
                    label: t("r2Cv"),
                    type: FIELD_TYPES.TextBox,
                    placeholder: t("R2CVPlaceholder"),
                    name: "R2CV",
                    dataType: DATA_TYPES.number,
                    show: formData.modelType !== MODEL_TYPE.calculatedFields,
                    required:
                        formData.modelType !== MODEL_TYPE.calculatedFields,
                    tooltip: t("r2CvTooltip"),
                },
                {
                    label: t("rmseTest"),
                    type: FIELD_TYPES.TextBox,
                    placeholder: t("rmseTestPlaceholder"),
                    name: "RMSETest",
                    dataType: DATA_TYPES.number,
                    show: formData.modelType !== MODEL_TYPE.calculatedFields,
                    required:
                        formData.modelType !== MODEL_TYPE.calculatedFields,
                    tooltip: t("rmseTestTooltip"),
                },
                {
                    label: t("R2Test"),
                    type: FIELD_TYPES.TextBox,
                    placeholder: t("R2TestPlaceholder"),
                    name: "R2Test",
                    dataType: DATA_TYPES.number,
                    show: formData.modelType !== MODEL_TYPE.calculatedFields,
                    required:
                        formData.modelType !== MODEL_TYPE.calculatedFields,
                    tooltip: t("r2TestTooltip"),
                },
                {
                    label: t("avgReadings"),
                    type: FIELD_TYPES.TextBox,
                    placeholder: t("numberOfMeasurementsPlaceholder"),
                    name: "avgReadings",
                    dataType: DATA_TYPES.number,
                    show: formData.modelType !== MODEL_TYPE.calculatedFields,
                    required:
                        formData.modelType !== MODEL_TYPE.calculatedFields,
                    tooltip: t("averageReadingsTooltip"),
                },
                {
                    label: t("avgPredictions"),
                    type: FIELD_TYPES.TextBox,
                    placeholder: t("numberOfMeasurementsPlaceholder"),
                    name: "avgPredictions",
                    dataType: DATA_TYPES.number,
                    show: formData.modelType !== MODEL_TYPE.calculatedFields,
                    required:
                        formData.modelType !== MODEL_TYPE.calculatedFields,
                    tooltip: t("averagePredictionsTooltip"),
                },
                {
                    label: t("BiasCV"),
                    type: FIELD_TYPES.TextBox,
                    placeholder: t("numberOfMeasurementsPlaceholder"),
                    name: "BiasCV",
                    dataType: DATA_TYPES.number,
                    show: formData.modelType !== MODEL_TYPE.calculatedFields,
                    tooltip: t("biasCvTooltip"),
                },
                {
                    label: t("BiasTest"),
                    type: FIELD_TYPES.TextBox,
                    placeholder: t("numberOfMeasurementsPlaceholder"),
                    name: "BiasTest",
                    dataType: DATA_TYPES.number,
                    show: formData.modelType !== MODEL_TYPE.calculatedFields,
                    tooltip: t("biasTestTooltip"),
                },
                {
                    label: t("RPDCV"),
                    type: FIELD_TYPES.TextBox,
                    placeholder: t("numberOfMeasurementsPlaceholder"),
                    name: "RPDCV",
                    dataType: DATA_TYPES.number,
                    show: formData.modelType !== MODEL_TYPE.calculatedFields,
                    tooltip: t("rpdCvTooltip"),
                },
                {
                    label: t("RPDTest"),
                    type: FIELD_TYPES.TextBox,
                    placeholder: t("numberOfMeasurementsPlaceholder"),
                    name: "RPDTest",
                    dataType: DATA_TYPES.number,
                    show: formData.modelType !== MODEL_TYPE.calculatedFields,
                    tooltip: t("rdpTestTooltip"),
                },
                {
                    label: t("numberOfSamples"),
                    type: FIELD_TYPES.TextBox,
                    placeholder: t("numberOfMeasurementsPlaceholder"),
                    name: "numberOfSamples",
                    dataType: DATA_TYPES.number,
                    show: formData.modelType !== MODEL_TYPE.calculatedFields,
                    tooltip: t("numberOfSamplesTooltip"),
                },
            ],
        },
        {
            label: t("minValue"),
            type: FIELD_TYPES.TextBox,
            placeholder: t("numberOfMeasurementsPlaceholder"),
            name: "minValue",
            dataType: DATA_TYPES.number,
            show: true,
            required: true,
        },
        {
            label: t("maxValue"),
            type: FIELD_TYPES.TextBox,
            placeholder: t("numberOfMeasurementsPlaceholder"),
            name: "maxValue",
            dataType: DATA_TYPES.number,
            show: true,
            required: true,
        },
        {
            label: t("numberOfMeasurements"),
            type: FIELD_TYPES.TextBox,
            placeholder: t("numberOfMeasurementsPlaceholder"),
            name: "numberOfMeasurements",
            dataType: DATA_TYPES.number,
            show: true,
            required: true,
        },
        {
            label: t("outlierDetectionType"),
            type: FIELD_TYPES.CheckBoxes,
            name: "outlierDetectionType",
            options: Object.values(OUTLIER_TYPES),
            show: formData.modelType === MODEL_TYPE.eigenvector,
            customValidationErrors: (
                <ValidationError
                    errorMessage={
                        error.QResidual ||
                        error.ReducedHotellingT2 ||
                        error.outlierType
                    }
                />
            ),
        },
        {
            label: t("outlierDetectionOperation"),
            type: FIELD_TYPES.RadioButton,
            name: "outlierDetectionOperation",
            show: formData.modelType === MODEL_TYPE.eigenvector,
            default: MODEL_TYPE.standard,
            options: [
                {
                    name: LOGIC_TYPE.and,
                    value: LOGIC_TYPE.and,
                    label: "AND",
                    disabled: !(
                        formData.QResidual?.booleanValue &&
                        formData.ReducedHotellingT2?.booleanValue
                    ),
                },
                {
                    name: LOGIC_TYPE.or,
                    value: LOGIC_TYPE.or,
                    label: "OR",
                    disabled: !(
                        formData.QResidual?.booleanValue &&
                        formData.ReducedHotellingT2?.booleanValue
                    ),
                },
            ],
            tooltip: t("outlierOperationTooltip"),
        },
        {
            label: t("operationsJson"),
            type: FIELD_TYPES.TextArea,
            placeholder: t("jsonPlaceholder", {
                field: "operations",
            }),
            name: "operationsJson",
            show: formData.modelType === MODEL_TYPE.standard,
            required: formData.modelType === MODEL_TYPE.standard,
        },
        {
            label: t("operationsFile"),
            type: FIELD_TYPES.File,
            accept: "text/xml",
            name: "operationsFile",
            show: formData.modelType === MODEL_TYPE.eigenvector,
            required: formData.modelType === MODEL_TYPE.eigenvector,
        },

        {
            label: t("operationsScreening"),
            type: FIELD_TYPES.TextArea,
            placeholder: t("jsonPlaceholder", {
                field: "operations screening",
            }),
            name: "operationsScreening",
            show: formData.modelType === MODEL_TYPE.standard,
            required: false,
        },
        {
            label: t("uploadResultsReport"),
            type: FIELD_TYPES.File,
            accept: "application/pdf",
            name: "resultsFile",
            show: true,
            required: false, //formData.modelType === MODEL_TYPE.standard,
        },
        {
            label: t("equation"),
            required: true,
            type: FIELD_TYPES.EquationBuilder,
            show: formData.modelType === MODEL_TYPE.calculatedFields,
            equationRequired:
                formData.modelType === MODEL_TYPE.calculatedFields,
        },
    ];

    const breadCrumbs = [
        {
            path: "/dashboard",
            element: () => <i className="fas fa-home" />,
        },
        {
            path: "/models",
            element: () => <Trans i18nKey="models">Models</Trans>,
        },
        {
            element: () => (
                <Trans i18nKey={editMode ? "editModel" : "uploadModel"}>
                    {editMode ? "Edit Model" : "Upload Model"}
                </Trans>
            ),
        },
    ];

    useEffect(() => {
        handleMissingData();
    }, []);

    useEffect(() => {
        localValidation();
    }, [formData]);

    useEffect(() => {
        if (model.modelId) {
            fetchVariables();
        }
    }, [model]);

    const handleMissingData = async () => {
        if (!location.state?.data) {
            history.push("/models");
        } else {
            setExistingModelType(modelType || "");
            setModel(location.state?.data);
            if (modelParameterVersionId) {
                setEditMode(true);
                const result = await ModelVersionsApi.getVersionById(
                    modelParameterVersionId
                );
                if (isEmpty(result)) {
                    history.push("/models");
                } else {
                    delete result?.modelInfo?.createdAt;
                    delete result?.modelInfo?.lastUpdatedAt;
                    delete result?.modelInfo?.deleted;
                    delete result?.modelInfo?.id;
                    setIsVisible(result?.modelInfo?.isVisible);
                    setFormattedEquation(result?.equation);
                    const operationsJSON =
                        result.operations?.data || result.operations;
                    setFormData({
                        modelType,
                        ...removeEmptyFields(result.modelInfo),
                        outlierDetectionType:
                            result.modelInfo?.outlierDetectionType,
                        QResidual: {
                            booleanValue: result.modelInfo?.outlierDetection
                                ?.QResidual
                                ? true
                                : false,
                            inputValue:
                                result?.modelInfo?.outlierDetection?.QResidual
                                    ?.threshold || 4,
                        },

                        ReducedHotellingT2: {
                            booleanValue: result.modelInfo?.outlierDetection
                                ?.ReducedHotellingT2
                                ? true
                                : false,
                            inputValue:
                                result?.modelInfo?.outlierDetection
                                    ?.ReducedHotellingT2?.threshold || 4,
                        },
                        outlierDetectionOperation:
                            result?.modelInfo?.outlierDetection?.operation,
                        operationsJson: operationsJSON,
                        numberOfMeasurements: result.numberOfMeasurements,
                        version: result.version,
                        description: result.description,
                        operationsScreening: result.operationsScreening
                            ? JSON.stringify(result.operationsScreening)
                            : "",
                        resultsFile: result.resultsFile,
                        operationsFile: result.operations?.file,
                    });
                }
            } else if (modelType) {
                setFormData({ ...formData, modelType });
            }
        }
    };

    const fetchVariables = async () => {
        const result = await LookupsAPI.getModelParametersByModelId(
            model.modelId
        );
        setVariables(
            result.map((item) => ({
                ...item,
                label:
                    `${item?.label}`?.trim()?.replace(/[+*/ -]/g, "_") ||
                    "unknown",
            }))
        );
    };

    const getJSON = (key) => {
        try {
            if (!isEmpty(formData[key])) {
                if (typeof formData[key] !== "string") return formData[key];
                return JSON.parse(formData[key]);
            }
            return [];
        } catch (e) {
            setError({ ...error, [key]: "invalid JSON input" });
            return "Invalid";
        }
    };

    const parseToJson = () => {
        return {
            ...formData,
            modelInfo: removeEmptyFields({
                executionOrder: +formData.executionOrder,
                RMSECalib: +formData.RMSECalib,
                R2Calib: +formData.R2Calib,
                RMSECV: +formData.RMSECV,
                R2CV: +formData.R2CV,
                RMSETest: +formData.RMSETest,
                R2Test: +formData.R2Test,
                avgReadings: +formData.avgReadings,
                avgPredictions: +formData.avgPredictions,
                BiasCV: +formData.BiasCV,
                BiasTest: +formData.BiasTest,
                RPDCV: +formData.RPDCV,
                RPDTest: +formData.RPDTest,
                ...(formData.numberOfSamples && {
                    numberOfSamples: +formData.numberOfSamples,
                }),
                minValue: +formData.minValue,
                maxValue: +formData.maxValue,
                isVisible: isVisible,
            }),
            operationsScreening: getJSON("operationsScreening"),
            ...(formData.modelType !== MODEL_TYPE.calculatedFields && {
                operationsJson: getJSON("operationsJson"),
            }),
        };
    };

    const extensionChecker = (file, extension) => {
        if (file) {
            let filenameArr = file.name?.split(".");
            if (
                filenameArr &&
                (filenameArr[filenameArr.length - 1] === extension ||
                    filenameArr[filenameArr.length - 1] ===
                        extension.toUpperCase())
            ) {
                return true;
            }
        }
        return false;
    };

    const localValidation = () => {
        const newErrorObject = {};
        const parsedFormData = parseToJson();
        if (
            formData?.resultsFile?.name &&
            !extensionChecker(formData.resultsFile, "pdf")
        ) {
            newErrorObject["resultsFile"] = "File must be of type .pdf";
        }
        if (
            formData.modelType === MODEL_TYPE.eigenvector &&
            !formData?.QResidual.booleanValue &&
            !formData?.ReducedHotellingT2.booleanValue
        ) {
            newErrorObject["outlierType"] =
                "Outlier detection types must have at least 1 item selected.";
        }
        if (
            formData?.operationsFile?.name &&
            !extensionChecker(formData.operationsFile, "xml")
        ) {
            newErrorObject["operationsFile"] =
                "Operations File must be of type .xml";
        }

        validate(
            parsedFormData,
            ModelSchema.UploadModelSchema,
            setIsFormValid,
            setError,
            {
                abortEarly: false,
                allowUnknown: false,
                context: {
                    isStandard: formData.modelType === MODEL_TYPE.standard,
                    isCalculatedField:
                        formData.modelType === MODEL_TYPE.calculatedFields,
                    isEigenvector:
                        formData.modelType === MODEL_TYPE.eigenvector,
                    operationsJson: formData.operationsJson ? true : false,
                    isFileAlreadyExists: isString(formData.resultsFile),
                    isOperationsFileAlreadyExists: isString(
                        formData.operationsFile
                    ),
                    isMultipleOutlierTypes:
                        formData.QResidual?.booleanValue &&
                        formData.ReducedHotellingT2?.booleanValue,
                },
            },
            newErrorObject,
            false
        );
    };

    const handleMinMaxValues = (parsedModelInfo) => {
        parsedModelInfo = removeEmptyFields(parsedModelInfo);
        return formData.modelType === MODEL_TYPE.calculatedFields
            ? {
                  ...parsedModelInfo,
                  minValue: +formData.minValue,
                  maxValue: +formData.maxValue,
                  executionOrder: -1,
                  isVisible,
              }
            : {
                  ...parsedModelInfo,
                  executionOrder: parsedModelInfo.executionOrder || 0,
                  isVisible,
              };
    };

    const removeEmptyFields = (modelInfo) => {
        Object.keys(modelInfo).forEach((key) => {
            if (
                !modelInfo[key] &&
                key !== "isVisible" &&
                isNaN(modelInfo[key])
            ) {
                delete modelInfo[key];
            }
        });
        return modelInfo;
    };

    const getIsFormValid = () => {
        return (
            isFormValid &&
            (formData.modelType === MODEL_TYPE.calculatedFields
                ? isEquationValid
                : true)
        );
    };

    const handleSubmitManageModel = async () => {
        setIsSubmitting(true);
        setIsFormSubmitted(true);
        const parsedFormData = parseToJson();

        const isVersionNameRepeated =
            parsedFormData.version &&
            (await uniqueModelVersionName(
                setError,
                setIsFormValid,
                model.id,
                parsedFormData.version,
                modelParameterVersionId
            ));

        if (
            !getIsFormValid() ||
            Object.keys(error).length > 0 ||
            isVersionNameRepeated
        ) {
            setIsSubmitting(false);
            return;
        }

        if (!parsedFormData) return;

        const operationsFile = {
            ...parsedFormData.operationsFile,
            name: getNameWithoutExtension(parsedFormData?.operationsFile?.name),
        };

        const resultsFile = {
            ...parsedFormData.resultsFile,
            name: getNameWithoutExtension(parsedFormData?.resultsFile?.name),
        };
        let uploadModelData = {
            modelType: parsedFormData.modelType,
            modelParameterId: model.id,
            modelInfo: handleMinMaxValues(parsedFormData.modelInfo),
            numberOfMeasurements: +parsedFormData.numberOfMeasurements,
            resultsFile,
            description: parsedFormData.description,
            version: parsedFormData.version,
            supportedAccessoryIds: parsedFormData.supportedAccessoryIds,
            operationsScreening: parsedFormData.operationsScreening,
            operations: {
                data: parsedFormData.operationsJson || [],
                file: operationsFile,
            },
            ...(formData.modelType === MODEL_TYPE.eigenvector && {
                outlierDetection: {
                    ...(formData.ReducedHotellingT2?.booleanValue && {
                        ReducedHotellingT2: {
                            threshold: +formData.ReducedHotellingT2?.inputValue,
                        },
                    }),
                    ...(formData.QResidual?.booleanValue && {
                        QResidual: {
                            threshold: +formData.QResidual?.inputValue,
                        },
                    }),
                    ...(formData.QResidual?.booleanValue &&
                        formData.ReducedHotellingT2?.booleanValue && {
                            operation: formData.outlierDetectionOperation,
                        }),
                },
            }),
        };

        if (formData.modelType === MODEL_TYPE.standard) {
            uploadModelData = {
                ...uploadModelData,
                isDataRobotModel: false,
            };
        } else if (formData.modelType === MODEL_TYPE.calculatedFields) {
            uploadModelData = {
                ...uploadModelData,
                equation: equationWidgetRef.current.getStringEquation(),
            };
        }

        if (editMode) {
            uploadModelData.id = modelParameterVersionId;
            updateModelVersion(uploadModelData);
        } else {
            uploadModel(uploadModelData);
        }
    };

    const updateModelVersion = (uploadModelData) => {
        ModelVersionsApi.updateVersion(uploadModelData)
            .then((res) => {
                if (res) {
                    SuccessAlert(t("editVersionSuccessMessage"));
                    LogCapture.info(
                        `Model updated with id: ${model?.id}`,
                        ENTITIES.modelParameters,
                        ACTIONS.modelParameters.updateModel
                    );
                    history.goBack();
                }
            })
            .catch((responseError) => {
                LogCapture.error(
                    `Couldn't edit model with with id: ${model?.id}, error: ${responseError}`
                );
                ErrorAlert(responseError);
            })
            .finally(() => {
                setIsSubmitting(false);
            });
    };

    const uploadModel = (uploadModelData) => {
        ModelParameterAPI.uploadModel(uploadModelData)
            .then((res) => {
                if (res) {
                    SuccessAlert(t("uploadVersionSuccessMessage"));
                    LogCapture.info(
                        `New model uploaded with id: ${model?.id}`,
                        ENTITIES.modelParameters,
                        ACTIONS.modelParameters.uploadModel
                    );
                    history.push("/models");
                }
            })
            .catch((responseError) => {
                LogCapture.error(
                    `Couldn't upload new model with with id: ${model?.id}, error: ${responseError}`
                );
                ErrorAlert(responseError);
            })
            .finally(() => {
                setIsSubmitting(false);
            });
    };

    return (
        <>
            <BreadCrumb crumbs={breadCrumbs} />
            <div className="product-header card-table shadow mb-5 flex items-center">
                <div className="flex items-center justify-between w-full">
                    <div className="flex items-center">
                        <LazyImage
                            src={
                                model.image ||
                                require("../../assets/images/neural.png")
                                    .default
                            }
                            className="card-header-img"
                            alt="model image"
                        />
                        <h3 className="medium-title-bold ml-3">
                            {model.modelName}
                        </h3>
                    </div>
                </div>
            </div>
            <div id="model-content" className="model-content">
                <div className="description-title product-info-description mb-6">
                    <h3>
                        <Trans i18nKey="description">Description</Trans>
                    </h3>
                    <p>{model.description}</p>
                </div>
                <div className="prod-title product-info-specification mb-6">
                    {/* <h3>Specifications</h3> */}
                    <ul className="specification-list flex justify-between">
                        <li className="input-group">
                            <label className="uppercase">
                                <Trans i18nKey="materialName">
                                    Material Name
                                </Trans>
                            </label>
                            <div className="specification-list-title blue-color">
                                {model.materialName}
                            </div>
                        </li>
                        <li className="input-group">
                            <label className="uppercase">
                                <Trans i18nKey="parameterName">
                                    Parameter Name
                                </Trans>
                            </label>
                            <div className="specification-list-title blue-color">
                                {model.parameterName}
                            </div>
                        </li>
                        <li className="input-group">
                            <label className="uppercase">
                                <Trans i18nKey="createdBy">Created By</Trans>
                            </label>
                            <div className="specification-list-title blue-color">
                                {model.createdBy || "---"}
                            </div>
                        </li>
                        <li className="input-group">
                            <label className="uppercase">
                                <Trans i18nKey="createdAt">Created At</Trans>
                            </label>
                            <div className="specification-list-title blue-color">
                                {longDate(model.createdAt)}
                            </div>
                        </li>
                    </ul>
                </div>
            </div>
            <header className="main-content-header flex items-center justify-between">
                <h3>
                    <Trans i18nKey={editMode ? "editModel" : "uploadModel"}>
                        {editMode ? "Edit Model" : "Upload Model"}
                    </Trans>
                </h3>
            </header>
            <div className="pb-2">
                <UploadModelCard
                    widgetRef={equationWidgetRef}
                    options={MODEL_OPTIONS}
                    formData={formData}
                    setFormData={setFormData}
                    error={error}
                    setError={setError}
                    isFormSubmitted={isFormSubmitted}
                    setIsVisible={setIsVisible}
                    isVisible={isVisible}
                    modelId={model.modelId}
                    isEquationValid={isEquationValid}
                    setIsEquationValid={setIsEquationValid}
                    formattedEquation={formattedEquation}
                    setFormattedEquation={setFormattedEquation}
                    externalVariables={variables}
                />
            </div>
            <div className="main-customer-form-bnt flex items-center justify-end">
                <Link to="/models">
                    <button className="secondary-blue-btn mr-2">
                        <Trans i18nKey="cancel">Cancel</Trans>
                    </button>
                </Link>
                <button
                    onClick={handleSubmitManageModel}
                    className={`default-orange-outline-btn ${
                        isSubmitting || (isFormSubmitted && !getIsFormValid())
                            ? "disabled"
                            : ""
                    }`}
                    disabled={
                        isSubmitting || (isFormSubmitted && !getIsFormValid())
                    }
                    id="upload-model-btn"
                >
                    {isSubmitting ? (
                        <>
                            <span className="animate-spin mr-3 inline-flex">
                                <i className="fas fa-sync"></i>
                            </span>
                            <Trans i18nKey="requesting">Requesting...</Trans>
                        </>
                    ) : (
                        <Trans i18nKey={editMode ? "editModel" : "uploadModel"}>
                            {editMode ? "Edit Model" : "Upload Model"}
                        </Trans>
                    )}
                </button>
            </div>
        </>
    );
};

export default ModelUpload;
